import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { catchError, Observable, of, shareReplay, take, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslatePipe } from '@ngx-translate/core';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

import { environment } from '@env';
import { PeriodComponent } from '../../v2/shared/components/period/period.component';
import {
  ComplianceCheckDetails,
  ComplianceCheckReason,
  ComplianceCheckStatus,
  ProcessComplianceCheckRequest,
  ComplianceCheckQuestionnaire,
  ComplianceCheckStandardSection,
  ComplianceCheckQuestion,
  standardSectionQuestionCounts,
  extensionSectionQuestionCounts,
  ComplianceCheckExtensionQuestion,
  ComplianceCheckExtensionSection,
  ComplianceCheckExtensionStartSection,
} from '~/app/wet-dba/compliance-check.models';
import { GlobalConstants } from '@v2/configs/global.constants';
import { TranslateExtensionService } from '@v2/core/services/translate-extension.service';
import { ChoiceComponent, ChoicesComponent } from '@v2/shared/components/choice/choice.component';
import { DateLabelComponent } from '@v2/shared/components/date-label/date-label.component';
import { CheckboxComponent } from '@v2/shared/components/checkbox/checkbox.component';
import { TextareaFieldComponent } from '@v2/shared/components/textarea-field/textarea-field.component';
import { ValidationGroup } from '@v2/core/validation/validationgroup';
import { AlertService } from '@v2/shared/components/alert/alert.service';
import { LocalDatePipe } from '@v2/shared/pipes/local-date.pipe';
import { ComplianceCheckAlreadyUsedComponent } from './error-pages/compliance-check-already-used/compliance-check-already-used.component';
import { ComplianceCheckExpiredComponent } from './error-pages/compliance-check-expired/compliance-check-expired.component';
import { TextFieldComponent } from '@v2/shared/components/text-field/text-field.component';
import { EmailFieldComponent } from '@v2/shared/components/email-field/email-field.component';
import { DatePickerComponent } from '@v2/shared/components/date-picker/date-picker.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-compliance-check',
  templateUrl: './compliance-check.component.html',
  styleUrls: ['./compliance-check.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatStepperModule,
    AsyncPipe,
    TranslatePipe,
    PeriodComponent,
    TextFieldComponent,
    TextareaFieldComponent,
    DatePickerComponent,
    EmailFieldComponent,
    ChoicesComponent,
    ChoiceComponent,
    DateLabelComponent,
    CheckboxComponent,
    LocalDatePipe,
    ComplianceCheckExpiredComponent,
    ComplianceCheckAlreadyUsedComponent,
  ],
})
export class ComplianceCheckComponent implements OnInit {
  @ViewChildren('formFields') formFields: QueryList<any> | null = null;
  @ViewChild('stepper') stepper!: MatStepper;

  complianceCheckDetails$!: Observable<ComplianceCheckDetails>;
  questionnaire = new ComplianceCheckQuestionnaire();

  isReasonExtension = false;
  disableSubmitButton = signal(false);
  isExpired = false;
  explanationMaxLength = 500;
  textFieldMaxLength = 100;
  today = new Date();
  todayThreeYearsLater = new Date(new Date().setFullYear(new Date().getFullYear() + 3));

  requiredGroup: ValidationGroup = new ValidationGroup();
  mustBeTrueGroup: ValidationGroup = new ValidationGroup();
  emailRequiredGroup: ValidationGroup = new ValidationGroup();

  private readonly portalBaseUrl = environment.uiBaseUrl;

  private readonly httpClient = inject(HttpClient);
  private readonly translateExtensionService = inject(TranslateExtensionService);
  private readonly alertService = inject(AlertService);
  private readonly localDatePipe = inject(LocalDatePipe);
  showAdditionalQuestionWhenAllQuestionsAnsweredNo = false;

  ngOnInit(): void {
    this.retrieveComplianceCheckDetails();
    this.setValidationGroups();
  }

  submit(): void {
    this.disableSubmitButton.set(true);
    if (this.isFormValid()) {
      this.emptyFieldsWhereNecessary();
      const processComplianceCheckRequest: ProcessComplianceCheckRequest = {
        questionnaire: this.questionnaire,
      };

      this.httpClient
        .post<void>(
          `${environment.v3.apiBaseUrl}/api/compliance-check/v1.0/process-compliance-check-answers`,
          processComplianceCheckRequest,
        )
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.alertService.success('compliance-check.questions.submit-success');
            this.redirectToLandingPage();
          },
          error: () => {
            this.alertService.error('compliance-check.questions.submit-failure');
            this.disableSubmitButton.set(false);
          },
        });
    } else {
      this.alertService.error('compliance-check.questions.submit-error');
      this.disableSubmitButton.set(false);
    }
  }

  isFormValid(): boolean {
    let valid = true;
    this.formFields!.forEach((element: any) => {
      if (!element.isValid()) {
        valid = false;
      }
    });

    return valid;
  }

  // A standard section is completed if:
  // - All questions' radio buttons have a value, either true or false
  // - If a question's answer is set to true (Yes), the associated explanation field should be filled
  isStandardSectionCompleted(section: ComplianceCheckStandardSection): boolean {
    return section.questions.every((q) => q.answer !== null && (q.answer === false || q.explanation.trim().length > 0));
  }

  // An extension section is completed if:
  // - All questions' text fields have a non-empty value
  isExtensionSectionCompleted(section: ComplianceCheckExtensionSection): boolean {
    return section.questions.every((q) => q.answer !== null && q.answer.trim().length > 0);
  }

  // The extension start section is completed if:
  // - Either the first question is answered with 'No'...
  // - ...or the first question is answered with 'Yes' and the rest of the questions are answered as well
  isExtensionStartSectionCompleted(extensionStartSection: ComplianceCheckExtensionStartSection): boolean {
    const willAssignmentBeExtended = extensionStartSection.willAssignmentBeExtended;
    if (willAssignmentBeExtended === null) {
      return false;
    }
    if (willAssignmentBeExtended === false) {
      return true;
    }
    return (
      extensionStartSection.expectedEndDateChangedReason.trim().length > 0 &&
      extensionStartSection.expectedEndDate !== null &&
      extensionStartSection.deliveriesExplanation.trim().length > 0
    );
  }

  additionalQuestionAnswered(): void {
    if (this.questionnaire.hasConfirmedNoChangesToAssignment === false) {
      this.disableSubmitButton.set(true);
      this.alertService.error('compliance-check.questions.error-message-when-additional-question-answered-no');
    } else {
      this.disableSubmitButton.set(false);
    }
  }

  onStepChange(event: StepperSelectionEvent): void {
    if (this.isReasonExtension) return;

    // Evaluate questionnaire if user clicks directly on the confirm and Send step instead of the Next section button
    const lastStepConfirmAndSendIndex = this.stepper.steps.length - 1;
    if (event.selectedIndex === lastStepConfirmAndSendIndex) {
      const allQuestions = this.questionnaire.standardSections.flatMap((section) => section.questions);
      this.showAdditionalQuestionWhenAllQuestionsAnsweredNo = allQuestions.every(
        (q) => q.answer !== null && q.answer === false,
      );
      //reset the additional question radio button and enable the Send button, when user answers one of the question
      if (!this.showAdditionalQuestionWhenAllQuestionsAnsweredNo) {
        this.disableSubmitButton.set(false);
        this.questionnaire.hasConfirmedNoChangesToAssignment = null;
      }
    }
  }

  // Initialises the questionnaire; based on the amount of questions per section,
  // the sections are added to the questionnaire object with default values
  private initQuestionnaire(): void {
    if (this.isReasonExtension) {
      this.questionnaire.extensionSections = extensionSectionQuestionCounts.map((questionCount, sectionIndex) => ({
        index: sectionIndex + 1,
        header: this.translateExtensionService.getInstantTranslation(
          `compliance-check.questions.extension.section-${sectionIndex + 1}.header`,
        ),
        questions: this.createExtensionSectionQuestions(sectionIndex + 1, questionCount),
      }));
    } else {
      this.questionnaire.standardSections = standardSectionQuestionCounts.map((questionCount, sectionIndex) => ({
        index: sectionIndex + 1,
        header: this.translateExtensionService.getInstantTranslation(
          `compliance-check.questions.section-${sectionIndex + 1}.header`,
        ),
        questions: this.createStandardSectionQuestions(sectionIndex + 1, questionCount),
      }));
    }
  }

  private createExtensionSectionQuestions(sectionIndex: number, count: number): ComplianceCheckExtensionQuestion[] {
    return Array.from({ length: count }, (_, i) => ({
      index: i + 1,
      text: this.translateExtensionService.getInstantTranslation(
        `compliance-check.questions.extension.section-${sectionIndex}.question-${i + 1}`,
      ),
      answer: '',
    }));
  }

  private createStandardSectionQuestions(sectionIndex: number, count: number): ComplianceCheckQuestion[] {
    return Array.from({ length: count }, (_, i) => ({
      index: i + 1,
      text: this.translateExtensionService.getInstantTranslation(
        `compliance-check.questions.section-${sectionIndex}.question-${i + 1}`,
      ),
      answer: null,
      explanation: '',
    }));
  }

  // For each question, check whether we should empty the (explanation) field(s) in case the answer is set to
  // an answer where we do not require the additional information
  // (e.g. Explanations could technically be filled when the user initially answered 'Yes', but later on changed it to 'No')
  private emptyFieldsWhereNecessary(): void {
    if (this.questionnaire!.isUserOriginalRecipient === true) {
      this.questionnaire!.submitter.firstName = '';
      this.questionnaire!.submitter.surname = '';
      this.questionnaire!.submitter.emailAddress = '';
    }

    this.questionnaire!.standardSections.forEach((section) => {
      section.questions.forEach((question) => {
        if (question.answer === false) {
          question.explanation = '';
        }
      });
    });
  }

  // After a few seconds, redirects the user to the landing page
  private redirectToLandingPage(): void {
    const waitTimeInSeconds = 5;
    const waitTime = waitTimeInSeconds * 1000;
    setTimeout(() => {
      location.href = this.portalBaseUrl;
    }, waitTime);
  }

  private retrieveComplianceCheckDetails(): void {
    this.complianceCheckDetails$ = this.httpClient
      .get<ComplianceCheckDetails>(`${environment.v3.apiBaseUrl}/api/compliance-check/v1.0/compliance-check-details`)
      .pipe(
        tap((details) => {
          const languageCode = details.languageCode!;
          localStorage.setItem(GlobalConstants.languageLocalStorageItemKey, languageCode);
          this.translateExtensionService.updateLanguageCode(languageCode);

          // Based on the language code, retrieve the correct translations for the sections and questions set in the models
          const contractEndDateString = this.localDatePipe.transform(details.contractEndDate, 'd MMM y');
          this.setNonStandardSectionTexts(contractEndDateString);
          this.isReasonExtension = details.reason === ComplianceCheckReason.Extension;
          this.initQuestionnaire();

          if (details.complianceCheckExpiryDate) {
            this.isExpired =
              new Date(details.complianceCheckExpiryDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
          }
        }),
        catchError(() => {
          return of({ status: ComplianceCheckStatus.NotSpecified } as ComplianceCheckDetails);
        }),
        shareReplay(1),
      );
  }

  private setNonStandardSectionTexts(contractEndDateString: string): void {
    this.questionnaire.extensionStartSection.header = this.translateExtensionService.getInstantTranslation(
      'compliance-check.questions.extension.section-start.header',
    );

    this.questionnaire.extensionStartSection.question1Text = this.translateExtensionService.getInstantTranslation(
      'compliance-check.questions.extension.section-start.question-1',
      {
        contractEndDate: contractEndDateString,
      },
    );

    this.questionnaire.extensionStartSection.question2Text = this.translateExtensionService.getInstantTranslation(
      'compliance-check.questions.extension.section-start.question-2',
    );

    this.questionnaire.extensionStartSection.question3Text = this.translateExtensionService.getInstantTranslation(
      'compliance-check.questions.extension.section-start.question-3',
    );

    this.questionnaire.extensionStartSection.question4Text = this.translateExtensionService.getInstantTranslation(
      'compliance-check.questions.extension.section-start.question-4',
    );

    this.questionnaire.completedTruthfullyDeclarationText = this.translateExtensionService.getInstantTranslation(
      'compliance-check.questions.section-final.completed-truthfully',
    );

    this.questionnaire.noChangesToAssignmentDeclarationText = this.translateExtensionService.getInstantTranslation(
      'compliance-check.questions.section-final.additional-question-when-all-questions-answered-no',
    );
  }

  private setValidationGroups(): void {
    this.requiredGroup.mustBeRequired();
    this.mustBeTrueGroup.mustBeTrue();

    // Not the exact same email regex we use throughout our application, but a simplified version
    const emailRegexPattern = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
    this.emailRequiredGroup.mustBeRequired().mustMatchPattern(emailRegexPattern);
  }
}
